import { AcceptIcon, Loader, ProviderConsumer, Text, ThemePrepared } from '@fluentui/react-northstar';
import { Communication } from '@fluentui/react-teams';
import { IColumn } from '@fluentui/react/lib/DetailsList';
import { DateTime } from 'luxon';
import React, { useCallback, useEffect, useState } from 'react';
import { Helmet } from "react-helmet";
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useSetState } from '../AppState';
import { MobileSettingsView } from '../automation/AutomationTable';
import { getCachedFeature } from '../shared/cache/FeatureCache';
import { Breadcrumbs } from '../shared/components/BreadcrumbNavigation';
import { DraggableList, DraggableListTextContentProps } from '../shared/components/table/DraggableList';
import { UpgradeNeeded } from '../shared/components/UpgradeNeeded';
import { ICustomProperty } from '../shared/interfaces/customproperties.interface';
import { platformService } from '../shared/services/platform.service';
import { CONTACT_ADMIN, DEFAULT_API_STATUS, DONE, ERROR, FILTERS_STRINGS, LICENSE_FEATURE, LOADING, PRODUCT_LICENSE, SETTINGS, SETTINGS_PAGES, SOMETHING_WENT_WRONG, TREE_LISTING_CONSTANTS } from '../shared/utils/constants';
import { toTitleCase } from '../shared/utils/helper';
import { CustomPropertyDataType_LABELS } from './utils/constants';
import draggableStyles from './../shared/components/tree/DraggableItem/DraggableItem.module.css';

export const CustomPropertiesList = () => {
  const { t } = useTranslation();
  const setAppState = useSetState();
  const api = new platformService();
  const history = useHistory();
  const [apiStatus, setAPIStatus] = useState<APIStatus>(DEFAULT_API_STATUS as APIStatus);
  const [isLicensedUser, setIsLicensedUser] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [queryId, setQueryId] = useState(0);
  const [propertyList, setPropertyList] = useState<any[]>([]);
  const [search, setSearch] = useState<string>('');
  const archiveStorageKey = "isArchivedCustomPropertiesList";
  const [showArchived, setShowArchived] = useState<boolean>(JSON.parse(localStorage.getItem('isArchivedCustomPropertiesList') || 'false'));
  const [isFeatureEnabled, setIsFeatureEnabled] = useState(true);

  const renderRecord = (key: string) => (item: ICustomProperty) => {
    switch (key) {
      case 'name':
        return <div onClick={() => { if (isLicensedUser) { setQueryId(item.Id); setOpenEdit(true); } }} className={`${isLicensedUser ? 'cursor-pointer' : 'cursor-default'}`}>
          <Text content={item.Name} color={`${isLicensedUser && 'brand'}`} />
          {item.IsArchived && (<span className={draggableStyles.Default}>
            {TREE_LISTING_CONSTANTS.archived}
          </span>)}
        </div>
      case 'id':
        return <Text content={item.PropertyId} />;
      case 'description':
        return <Text content={item.Description} />;
      case 'dataType':
        return <Text content={CustomPropertyDataType_LABELS[item.DataType]} />;
      case 'required':
        return item.IsRequired ? <AcceptIcon style={{ paddingLeft: '25px' }} /> : <></>;
      case 'hidden': 
        return item.IsHidden ? <AcceptIcon style={{ paddingLeft: '25px' }} /> : <></> 
      case 'createddate':
        return <Text content={DateTime.fromJSDate(new Date(item.CreatedDate)).toLocaleString(DateTime.DATETIME_SHORT)} />;
      case 'editedby':
        return <Text content={item.ModifiedBy.DisplayName} />;
      case 'createdby':
        return <Text content={item.CreatedBy.DisplayName} />;
    }
    return <></>;
  };
  const columns: IColumn[] = [
    { key: 'name', name: t('custom-properties.fields.name'), minWidth: 120, maxWidth: 300, isMultiline: false, onRender: renderRecord('name'), isResizable: false, isCollapsible: false },
    { key: 'id', name: t('custom-properties.fields.id'), minWidth: 120, maxWidth: 300, isMultiline: false, onRender: renderRecord('id'), isResizable: false, isCollapsible: false },
    { key: 'description', name: t('custom-properties.fields.description'), minWidth: 130, maxWidth: 300, isMultiline: false, onRender: renderRecord('description'), isResizable: false, isCollapsible: false },
    { key: 'dataType', name: t('custom-properties.fields.data-type'), minWidth: 100, maxWidth: 300, isMultiline: false, onRender: renderRecord('dataType'), isResizable: false, isCollapsible: false },
    { key: 'required', name: t('custom-properties.fields.is-required'), minWidth: 90, maxWidth: 90, isMultiline: false, onRender: renderRecord('required'), isResizable: false, isCollapsible: false },
    { key: 'hidden', name: t('custom-properties.fields.is-hidden'), minWidth: 75, maxWidth: 75, isMultiline: false, onRender: renderRecord('hidden'), isResizable: false, isCollapsible: false },
    { key: 'createdby', name: t('custom-properties.fields.created-by'), minWidth: 120, maxWidth: 225, isMultiline: false, onRender: renderRecord('createdby'), isResizable: false, isCollapsible: false },
    { key: 'editedby', name: t('custom-properties.fields.edited-by'), minWidth: 100, maxWidth: 225, isMultiline: false, onRender: renderRecord('editedby'), isResizable: false, isCollapsible: false },
    { key: 'createddate', name: t('custom-properties.fields.created-date'), minWidth: 100, maxWidth: 225, isMultiline: false, onRender: renderRecord('createddate'), isResizable: false, isCollapsible: false }
  ];

  const fetchData = useCallback(async () => {
    setAPIStatus(prev => ({ ...prev, status: LOADING, msg: t('custom-properties.get-all-items') }));
    try {
      
      const ifArchived = showArchived ? '?' : `${FILTERS_STRINGS.ARCHIVE_FALSE}&`;
      const result = await Promise.all([
        getCachedFeature(LICENSE_FEATURE.CustomProperties),
        getCachedFeature(PRODUCT_LICENSE.AnalystAccess),
        api.getCustomProperties(`${ifArchived}${FILTERS_STRINGS.CUSTOM_PROPERTY_LIST}&$expand=ModifiedBy,CreatedBy&$orderby=Order asc&v=${new Date().getTime()}`)
      ]);
      setIsFeatureEnabled(result[0]);
      const { data: { value } } = result[2];
      setPropertyList(value);
      const propsFiltered: ICustomProperty[] = value;
      setAppState((prev) => ({
        ...prev,
        customProperties: propsFiltered.filter(p => !p.IsArchived)
      }));

      setIsLicensedUser(result[1]);
      setAPIStatus(prev => ({ ...prev, status: DONE }));
    } catch (err: any) {
      setAPIStatus(prev => ({ ...prev, status: ERROR, errCode: err.response.status }));
    }
  }, [showArchived]);

  
  const reorderItems = useCallback(async (items) => {
    try {
      await api.orderCustomProperties(items.map(x => x.Id));
      toast.success(t('custom-properties.changes-saved'));
    } catch (err: any) {
      console.log(`Exception while rearranging custom properties: ${err}`);
      toast.error(t('custom-properties.error-saving-changes'));
    }
  }, []);

  const deleteCustomPropertyApi = (id) => api.deleteCustomProperty(id);
  const archiveCustomPropertyApi = (item) => {
    delete item.CustomPropertyListConfiguration;
    delete item.CustomPropertyListItems;
    delete item.ModifiedBy;
    delete item.CreatedBy;

    const result = api.updateCustomProperty(item);
    result.then(() => fetchData());
    return result;
  }

  useEffect(() => { fetchData(); }, [showArchived]);

  useEffect(() => {  
    if(openEdit){
      const path = `/settings/custom-properties?id=${queryId}`;
      history.push(path); 
    }
  }, [openEdit]);

  const navs: Breadcrumbs = {
    breadcrumbs: [
      {
        title: t('common.settings'),
        link: '/settings'
      },
      {
        title: t('custom-properties.menu-title'),
        link: ''
      }
    ]
  };

  const textContents: DraggableListTextContentProps = {
    pageTitle: t('custom-properties.title'),
    deleteItem: t('custom-properties.deleting-item'),
    updateItem: t('custom-properties.updating-item'),
    emptyHeader: t('custom-properties.empty-header'),
    emptySubHeader: t('custom-properties.empty-subheader'),    
    archiveStorageKey: archiveStorageKey
  };

  const renderContent = (status: APIStatus, globalTheme: ThemePrepared<any>) => {
    if (status.status === LOADING)
      return <Loader label={apiStatus.msg} className='h-screen' />

    if (status.status === ERROR) {
      return (status.errCode === 402) ?
        <UpgradeNeeded toolbarText={`${t('common.settings')} > ${t('custom-properties.title')}`} breadcrumbs={navs} headerText={t('custom-properties.payment.header')} subheaderText={t('custom-properties.payment.subheader')} /> :
        <Communication fields={{ title: SOMETHING_WENT_WRONG, desc: CONTACT_ADMIN }} />;
    }

    const filteredList = propertyList.filter(x => search === "" || x.Name?.toLowerCase().includes(search.toLowerCase()) || x.Description?.toLowerCase().includes(search.toLowerCase()));

    return <DraggableList 
        columns={columns} 
        itemList={propertyList}
        setItemList={setPropertyList}
        search={search}
        setSearch={setSearch}
        showArchived={showArchived}
        setShowArchived={setShowArchived}
        reorderItemsCallback={reorderItems}
        filteredList={filteredList}
        isLicensedUser={isLicensedUser}
        queryId={queryId}
        setQueryId={setQueryId}
        setOpenEdit={setOpenEdit}
        navs={navs}
        setAPIStatus={setAPIStatus}
        fetchDataCallback={fetchData}
        deleteItemAPI={deleteCustomPropertyApi}
        archiveItemAPI={archiveCustomPropertyApi}
        textContents={textContents}
      />;
  };

  return (<ProviderConsumer render={(globalTheme) => (<>
    <Helmet>
      <title>{t('custom-properties.title')}</title>
    </Helmet>
    {(!isFeatureEnabled) ? <UpgradeNeeded
      toolbarText={`${toTitleCase(SETTINGS)} > ${toTitleCase(SETTINGS_PAGES.CUSTOM_PROPERTIES)}`}
      headerText={t('custom-properties.no-access-current-plan')}
      subheaderText={t('custom-properties.no-access-upgrade-plan')}/> : 
      <>
      <div className='hidden md:block'>{renderContent(apiStatus, globalTheme)}</div>
      <MobileSettingsView />
      </>
    }
  </>)} 
  />);
}